import { useMemo } from 'react'
import { CheckCircle2, Trophy, AlertTriangle, Clock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import {
  computeAutoFinalizedCount,
  computeClosingStreak,
  computeLongestStreak,
  computeMissedDaysThisMonth,
} from './dayCloseHelpers'
import type { BusinessDay } from '@/types'

interface ClosingHealthMetricsProps {
  history: BusinessDay[] | undefined
}

/**
 * Owner-facing reliability dashboard sitting above the Day History
 * table. Four metrics, all derived on the fly from `history` — nothing
 * is persisted as a counter.
 *
 *  • Current streak: matches the check-mark row on the Daily Closing page.
 *  • Longest streak (90d): peak consistency over the lookback window.
 *  • Missed days this month: weekdays + weekends from 1st through
 *    yesterday with no closed business_day row. Doesn't model planned
 *    closures — a Sunday-off shop will see Sundays counted here until
 *    a holiday-calendar config exists.
 *  • Auto-finalized (30d): closed_by IS NULL && status='closed' within
 *    the window. Catches days that the auto-finalize sweeper locked
 *    because no manager ran Close Day Wizard before the closeout hour.
 */
export function ClosingHealthMetrics({ history }: ClosingHealthMetricsProps) {
  const metrics = useMemo(() => {
    return {
      currentStreak: computeClosingStreak(history),
      longestStreak: computeLongestStreak(history, 90),
      missedThisMonth: computeMissedDaysThisMonth(history),
      autoFinalized: computeAutoFinalizedCount(history, 30),
    }
  }, [history])

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Tile
        icon={<CheckCircle2 className="h-4 w-4" fill="currentColor" stroke="white" strokeWidth={2.5} />}
        label="Current streak"
        value={metrics.currentStreak}
        unit={metrics.currentStreak === 1 ? 'day' : 'days'}
        tone="emerald"
        hint={metrics.currentStreak === 0 ? 'Close today to start one' : undefined}
      />
      <Tile
        icon={<Trophy className="h-4 w-4" />}
        label="Longest streak"
        value={metrics.longestStreak}
        unit={metrics.longestStreak === 1 ? 'day' : 'days'}
        tone="amber"
        hint="last 90 days"
      />
      <Tile
        icon={<AlertTriangle className="h-4 w-4" />}
        label="Missed this month"
        value={metrics.missedThisMonth}
        unit={metrics.missedThisMonth === 1 ? 'day' : 'days'}
        tone={metrics.missedThisMonth > 0 ? 'rose' : 'muted'}
        hint={metrics.missedThisMonth === 0 ? 'Perfect month so far' : 'No close recorded'}
      />
      <Tile
        icon={<Clock className="h-4 w-4" />}
        label="Auto-finalized"
        value={metrics.autoFinalized}
        unit={metrics.autoFinalized === 1 ? 'day' : 'days'}
        tone={metrics.autoFinalized > 0 ? 'amber' : 'muted'}
        hint={metrics.autoFinalized === 0 ? 'last 30 days' : 'No manager close — last 30d'}
      />
    </div>
  )
}

type Tone = 'emerald' | 'amber' | 'rose' | 'muted'

function Tile({
  icon,
  label,
  value,
  unit,
  tone,
  hint,
}: {
  icon: React.ReactNode
  label: string
  value: number
  unit: string
  tone: Tone
  hint?: string
}) {
  const iconWrap = cn(
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
    tone === 'emerald' &&
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
    tone === 'amber' &&
      'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
    tone === 'rose' &&
      'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
    tone === 'muted' && 'bg-muted text-muted-foreground',
  )
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={iconWrap}>{icon}</div>
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </div>
            <div className="mt-0.5 flex items-baseline gap-1">
              <span className="text-2xl font-bold tabular-nums">{value}</span>
              <span className="text-xs font-medium text-muted-foreground">{unit}</span>
            </div>
            {hint && (
              <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
